import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAccessTier } from '../contexts/AccessTierContext'
import { FaCrown, FaStar, FaShieldAlt } from 'react-icons/fa'

export function TierBadge({ showDetails = false, className = '' }) {
  const { userTier, tokensToNextTier, isConnected, tiers } = useAccessTier()
  const [showTooltip, setShowTooltip] = useState(false)

  if (!isConnected || !userTier) {
    return null
  }

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'Gold':
        return <FaCrown className="text-yellow-400" />
      case 'Silver':
        return <FaStar className="text-gray-300" />
      case 'Bronze':
        return <FaShieldAlt className="text-orange-400" />
      default:
        return null
    }
  }

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`inline-flex items-center space-x-2 ${className}`}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Tier Badge */}
        <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${userTier.color} border ${userTier.borderColor} cursor-pointer hover:scale-105 transition-transform`}>
          <span className="text-xl">{userTier.icon}</span>
          <span className="text-black font-bold text-sm">{userTier.name} Tier</span>
        </div>

        {/* Next Tier Info */}
        {showDetails && tokensToNextTier > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-xs text-slate-400"
          >
            {tokensToNextTier.toLocaleString()} $BODY to {userTier.name === 'Bronze' ? 'Silver' : 'Gold'}
          </motion.div>
        )}
      </motion.div>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-full left-0 mt-2 w-80 z-50"
          >
            <div className="bg-slate-900 border border-electric-blue/30 rounded-xl p-4 shadow-2xl shadow-electric-blue/20">
              <h4 className="text-white font-bold mb-2">Three-Tier Access System</h4>
              
              {/* All Tiers Info */}
              <div className="space-y-2">
                {Object.values(tiers).map((tier, index) => (
                  <div
                    key={index}
                    className={`flex items-start space-x-2 p-2 rounded-lg ${
                      tier.name === userTier.name 
                        ? 'bg-electric-blue/10 border border-electric-blue/30' 
                        : 'bg-slate-800/50'
                    }`}
                  >
                    <span className="text-xl flex-shrink-0">{tier.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-bold text-sm bg-gradient-to-r ${tier.color} bg-clip-text text-transparent`}>
                          {tier.name} Tier
                        </span>
                        {tier.name === userTier.name && (
                          <span className="text-xs text-electric-blue font-bold">YOU</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        {tier.min === 0 ? '0' : tier.min.toLocaleString()} - {tier.max === Infinity ? '∞' : tier.max.toLocaleString()} $BODY
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {tier.benefits.length} benefits • {tier.name === 'Gold' ? '15%' : tier.name === 'Silver' ? '5%' : '0%'} discount
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Current Status */}
              {tokensToNextTier > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <p className="text-xs text-slate-400">
                    Need <span className="text-white font-bold">{tokensToNextTier.toLocaleString()} more $BODY</span> to reach {userTier.name === 'Bronze' ? 'Silver' : 'Gold'}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export function TierInfoCard() {
  const { userTier, tokensToNextTier, tiers, isConnected } = useAccessTier()

  if (!isConnected) {
    return (
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <p className="text-slate-400 text-center">
          Connect your wallet to see your access tier
        </p>
      </div>
    )
  }

  if (!userTier) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${userTier.bgColor} border ${userTier.borderColor} rounded-xl p-6`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-4xl">{userTier.icon}</span>
          <div>
            <h3 className={`text-2xl font-black bg-gradient-to-r ${userTier.color} bg-clip-text text-transparent`}>
              {userTier.name} Tier
            </h3>
            <p className="text-slate-400 text-sm">Your current access level</p>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="mb-4">
        <h4 className="text-white font-bold mb-2">Your Benefits:</h4>
        <ul className="space-y-1">
          {userTier.benefits.map((benefit, index) => (
            <li key={index} className="text-slate-300 text-sm flex items-start">
              <span className="text-green-400 mr-2">✓</span>
              {benefit}
            </li>
          ))}
        </ul>
      </div>

      {/* Upgrade Prompt */}
      {tokensToNextTier > 0 && (
        <div className="pt-4 border-t border-slate-700/50">
          <p className="text-slate-400 text-sm mb-2">
            Need <span className="text-white font-bold">{tokensToNextTier.toLocaleString()} more $BODY</span> to reach{' '}
            <span className={`font-bold bg-gradient-to-r ${userTier.name === 'Bronze' ? tiers.SILVER.color : tiers.GOLD.color} bg-clip-text text-transparent`}>
              {userTier.name === 'Bronze' ? 'Silver' : 'Gold'} Tier
            </span>
          </p>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className={`bg-gradient-to-r ${userTier.color} h-2 rounded-full transition-all duration-500`}
              style={{ 
                width: `${Math.min(((parseFloat(tokenBalance.replace(/,/g, '')) || 0) / (userTier.name === 'Bronze' ? tiers.SILVER.min : tiers.GOLD.min)) * 100, 100)}%` 
              }}
            ></div>
          </div>
        </div>
      )}

      {/* Max Tier Message */}
      {userTier.name === 'Gold' && (
        <div className="pt-4 border-t border-slate-700/50">
          <p className="text-yellow-400 text-sm font-bold flex items-center">
            <FaCrown className="mr-2" />
            You're at the highest tier! Enjoy all premium benefits!
          </p>
        </div>
      )}
    </motion.div>
  )
}

// Helper to fix tokenBalance reference
function tokenBalance() {
  const { tokenBalance } = useAccessTier()
  return tokenBalance
}
